import { Box, Icon, IconButton, Paragraph } from '@zillow/constellation-10';
import { IconNotePlusFilled } from '@zillow/constellation-icons';
import { Col } from '@zg-rentals/zrs-comet';
import { format } from 'date-fns';
import { useContext } from 'react';
import { useRenterProfileNotesDataQuery } from '../../../../types/GraphqlTypes';
import { ModalType } from '../../../../types/ModalType';
import {
  emitNotesButtonClickEvent,
  NOTES_BUTTON_CLICK_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/notesModal';
import useModal from '../../../../hooks/useModal';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import NotesModal from '../../../Modals/NotesModal';
import Bone from '../../../Bone';
import TourStatusCard from '../../../TourStatusCard';
import { useAuth } from '@zg-rentals/auth-comet';
import ReadMoreText from '../../../ReadMoreText';

export const PLACEHOLDER_TEXT = 'Add note e.g. tour feedback';
export const TIMESTAMP_FORMAT_PATTERN = 'MMM d, h:mm a';

const NoteSection = (): JSX.Element | null => {
  const { conversationId, clientTimeZone, listingAlias } = useContext(InboxPageContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();

  const { data: notesData } = useRenterProfileNotesDataQuery({
    variables: {
      conversationId: conversationId || '',
      listingAlias: listingAlias || '',
    },
    fetchPolicy: 'cache-only',
  });

  if (!notesData) {
    return <NoteSectionSkeleton />;
  }
  let tourScheduledTag = '',
    applicationSentTag = '';
  const { applicationAlreadySent, attributes, touring } = notesData.ZRM_getConversationById;
  const hasZrmToursEnabled = touring.hasZrmToursEnabled ?? false;
  const hasPersonallyNotedATour = Boolean(attributes?.tourScheduledTimestampMs);
  const shouldShowPersonallyNotedTour = hasPersonallyNotedATour && !hasZrmToursEnabled;
  const noteContent = notesData.ZRM_getConversationById.note.note;
  const shouldShowTags =
    !!applicationAlreadySent || !!attributes?.applicationMarkedSent || shouldShowPersonallyNotedTour;
  const shouldShowTourStatusCard = hasZrmToursEnabled && listingAlias && conversationId;

  if (applicationAlreadySent) {
    applicationSentTag = 'Application sent using Zillow';
  } else if (attributes?.applicationMarkedSent) {
    applicationSentTag = 'Application sent';
  }

  if (shouldShowPersonallyNotedTour) {
    const formattedTimestamp = attributes?.tourScheduledTimestampMs
      ? format(+attributes?.tourScheduledTimestampMs, TIMESTAMP_FORMAT_PATTERN)
      : '';
    tourScheduledTag = `Tour scheduled ${formattedTimestamp}`;
  }

  const handleNotesSectionClick = (): void => {
    emitNotesButtonClickEvent(user, { listingAlias });
    setActiveModal(ModalType.NotesModal);
  };

  return (
    <>
      {activeModal === ModalType.NotesModal && conversationId && listingAlias && (
        <NotesModal
          clientTimeZone={clientTimeZone}
          conversationId={conversationId}
          listingAlias={listingAlias}
          shouldRefetchStatusLabels={true}
        />
      )}

      <Box css={{ marginBlock: 'loose' }}>
        <Box css={{ marginBottom: 'default' }}>
          <Box
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
            }}
          >
            <Paragraph
              textStyle="body-sm-bold"
              css={{
                color: 'text.subtle',
              }}
            >
              Notes
            </Paragraph>
            <IconButton
              data-eventid={NOTES_BUTTON_CLICK_EVENT_TYPE_ID}
              onClick={handleNotesSectionClick}
              aria-haspopup="dialog"
              emphasis="bare"
              shape="square"
              size="md"
              title="Add note"
              tone="brand"
            >
              <Icon>
                <IconNotePlusFilled />
              </Icon>
            </IconButton>
          </Box>

          {noteContent ? (
            <Paragraph>
              <ReadMoreText text={noteContent} maxLines={4} />
            </Paragraph>
          ) : (
            <Paragraph css={{ color: 'text.subtle' }}>{PLACEHOLDER_TEXT}</Paragraph>
          )}
        </Box>

        {shouldShowTags && (
          <>
            <Paragraph
              textStyle="body-sm-bold"
              css={{
                color: 'text.subtle',
              }}
            >
              Tags
            </Paragraph>

            {applicationSentTag && <Paragraph css={{ marginBlock: 'tighter' }}>{applicationSentTag}</Paragraph>}
            {tourScheduledTag && <Paragraph css={{ marginBlock: 'tighter' }}>{tourScheduledTag}</Paragraph>}
          </>
        )}

        {shouldShowTourStatusCard && (
          <Box css={{ marginTop: 'default' }}>
            <TourStatusCard conversationId={conversationId} listingAlias={listingAlias} showStackedView={true} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default NoteSection;

export const NoteSectionSkeleton = () => (
  <Box css={{ marginBlock: 'loose' }}>
    <>
      <Col xs={4}>
        <Bone dark={true} type="line" />
        <br />
      </Col>
      <Col xs={12}>
        <Bone dark={true} type="line" />
        <br />
      </Col>
      <Col xs={7}>
        <Bone dark={true} type="line" />
        <br />
      </Col>
    </>
  </Box>
);
